import React, { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { get, post } from '../../utils/ajax';
import { remoteRoutes } from '../../data/constants';
import {
  PHONE_HELPER_TEXT,
  PHONE_INPUT_PROPS,
  normalizePhone,
  phoneError,
  sanitizePhoneInput,
} from '../../utils/phoneHelpers';
import { fontFamilies } from '../../theme/fonts';

import { colors } from '../../theme/tokens';
type Status = 'loading' | 'ready' | 'submitting' | 'done' | 'error';

const EventCheckinPublic: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<Status>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [session, setSession] = useState<{
    eventTitle: string;
    eventDate: string;
    hubName: string;
  } | null>(null);
  const [alreadyIn, setAlreadyIn] = useState(false);
  const [alreadyEnrolled, setAlreadyEnrolled] = useState(false);
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    get(
      `${remoteRoutes.eventCheckinSessionByToken}/${token}`,
      (data: any) => {
        setSession(data);
        setStatus('ready');
      },
      () => {
        setErrorMsg(
          'This check-in link is not active. Ask the organizer for a new one.',
        );
        setStatus('error');
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = () => {
    if (!fullName.trim()) return;
    if (phoneError(phone)) return;
    setStatus('submitting');
    post(
      `${remoteRoutes.eventCheckinSessionByToken}/${token}/checkin`,
      {
        fullName: fullName.trim(),
        phone: phone.trim() ? normalizePhone(phone) : undefined,
        email: email.trim() || undefined,
      },
      (data: any) => {
        setAlreadyIn(!!data?.alreadyCheckedIn);
        setAlreadyEnrolled(!!data?.alreadyEnrolled);
        setStatus('done');
      },
      () => {
        setErrorMsg('Could not check you in. Please try again.');
        setStatus('error');
      },
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      style={{ background: colors.tintOrange, padding: 24 }}
    >
      <Box
        style={{
          background: colors.bgSurface,
          borderRadius: 24,
          padding: '40px 32px',
          maxWidth: 400,
          width: '100%',
          textAlign: 'center',
          boxShadow: `0 8px 40px ${colors.shadow}`,
        }}
      >
        <Box mb={3}>
          <div
            style={{
              width: 32,
              height: 3,
              borderRadius: 4,
              background: `linear-gradient(90deg, ${colors.progressFill} 0%, ${colors.accentOrange} 100%)`,
              margin: '0 auto 8px',
            }}
          />
          <Typography
            style={{
              fontFamily: '"Inter Tight", sans-serif',
              fontWeight: 800,
              fontSize: 15,
              color: colors.textPrimary,
            }}
          >
            Elevate Academy
          </Typography>
        </Box>

        {status === 'loading' && (
          <CircularProgress style={{ color: colors.progressFill }} size={48} />
        )}

        {(status === 'ready' || status === 'submitting') && session && (
          <>
            <Typography
              style={{
                fontFamily: fontFamilies.display,
                fontWeight: 700,
                fontSize: 17,
                color: colors.textPrimary,
                marginBottom: 4,
              }}
            >
              {session.eventTitle}
            </Typography>
            <Typography
              style={{
                fontSize: 13,
                color: colors.textMuted,
                marginBottom: 20,
              }}
            >
              {session.hubName}
              {session.eventDate
                ? ` · ${format(new Date(session.eventDate), 'EEE, MMM d')}`
                : ''}
            </Typography>

            <TextField
              label="Your name"
              fullWidth
              variant="outlined"
              size="small"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              style={{ marginBottom: 12 }}
            />
            <TextField
              label="Email (optional)"
              fullWidth
              variant="outlined"
              size="small"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ marginBottom: 12 }}
            />
            <TextField
              label="Phone (optional)"
              fullWidth
              variant="outlined"
              size="small"
              value={phone}
              onChange={(e) => setPhone(sanitizePhoneInput(e.target.value))}
              style={{ marginBottom: 20 }}
              inputProps={PHONE_INPUT_PROPS}
              error={!!phoneError(phone)}
              helperText={phoneError(phone) || PHONE_HELPER_TEXT}
            />
            <Button
              variant="contained"
              fullWidth
              disabled={
                !fullName.trim() ||
                !!phoneError(phone) ||
                status === 'submitting'
              }
              style={{
                borderRadius: 10,
                background: colors.progressFill,
                color: colors.onStat,
                textTransform: 'none',
                fontWeight: 700,
                padding: '10px 0',
              }}
              onClick={handleSubmit}
            >
              {status === 'submitting' ? 'Checking you in…' : 'Check In'}
            </Button>
          </>
        )}

        {status === 'done' && (
          <>
            <Box
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: colors.tintGreen,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: 32,
              }}
            >
              ✅
            </Box>
            <Typography
              style={{
                fontFamily: fontFamilies.display,
                fontWeight: 700,
                fontSize: 17,
                color: colors.onGreen,
              }}
            >
              {alreadyEnrolled
                ? 'Welcome back!'
                : alreadyIn
                ? "You're already checked in!"
                : "You're checked in!"}
            </Typography>
            <Typography
              style={{
                fontSize: 14,
                color: colors.textMuted,
                marginTop: 6,
              }}
            >
              {alreadyEnrolled
                ? `Thanks for coming, ${
                    fullName.split(' ')[0]
                  } — you're already one of our students!`
                : `Thanks for coming, ${fullName.split(' ')[0]}.`}
            </Typography>
          </>
        )}

        {status === 'error' && (
          <>
            <Box
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: colors.tintRed,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: 32,
              }}
            >
              ❌
            </Box>
            <Typography
              style={{
                fontFamily: fontFamilies.display,
                fontWeight: 700,
                fontSize: 15,
                color: colors.accentRed,
              }}
            >
              Something went wrong
            </Typography>
            <Typography
              style={{
                fontSize: 14,
                color: colors.textMuted,
                marginTop: 6,
              }}
            >
              {errorMsg}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default EventCheckinPublic;
